import type { MaybeRefOrGetter } from 'vue';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { getErrorStatusCode, isConnectionFailure } from '#layers/base/public';
import { ErrorDetailDialog } from '#layers/ui/public';

export interface UseListingErrorOptions {
	/** The failure to describe. Anything `ErrorDetailDialog` can format. */
	error: MaybeRefOrGetter<unknown>;
	/** Whether the view carries unsaved changes, making a rollback something to offer. */
	canResetView?: MaybeRefOrGetter<boolean | undefined>;
}

/**
 * What the user reads is ours and translated; whatever actually failed stays behind `showDetails`.
 * The engine's own message is English prose written for developers, so it belongs in the dialog
 * rather than on the listing itself.
 */
export function useListingError(options: UseListingErrorOptions) {
	const { error, canResetView } = options;

	const { t } = useI18n();
	const overlay = useOverlay();

	const isUnreachable = computed(() => isConnectionFailure(toValue(error)));

	const icon = computed(() => isUnreachable.value ? 'lucide:unplug' : 'lucide:circle-alert');

	const title = computed(() => isUnreachable.value
		? t('errors.listing.connection_failed.title')
		: t('errors.listing.load_failed.title'));

	const description = computed(() => isUnreachable.value
		? t('errors.listing.connection_failed.description')
		: t('errors.listing.load_failed.description'));

	/**
	 * The engine answered and refused the request itself, so the query is what has to change. Sending
	 * the same bytes again only reproduces the refusal — an invalid filter or sort is rejected every
	 * time. Matches the contract the stack retries against, where only 502/503/504 are worth another
	 * attempt and every 4xx is permanent.
	 */
	const isRejected = computed(() => {
		const statusCode = getErrorStatusCode(toValue(error));
		return statusCode !== undefined && statusCode >= 400 && statusCode < 500;
	});

	/** Another attempt is only worth offering where one could plausibly land differently. */
	const showRetry = computed(() => !isRejected.value);

	/**
	 * Rolling the view back only helps when the query itself is what failed. A request that never
	 * arrived was never evaluated, so offering it there would trade the user's filters for nothing.
	 *
	 * TODO widen to `!isTransientError` once #3352 lands. A 502/503/504 was not evaluated either,
	 * but that helper is what draws the line and does not exist on this branch yet.
	 */
	const showResetView = computed(() => !!toValue(canResetView) && !isUnreachable.value);

	return {
		icon,
		title,
		description,
		showRetry,
		showResetView,
		showDetails,
	};

	function showDetails() {
		overlay.create(ErrorDetailDialog, {
			props: {
				error: toValue(error),
			},
		}).open();
	}
}
